// ONE contract balance for Monthly management (audit DUP-06 / CON-04 / CON-05 / CON-11 / CON-24, owner
// 26 Sep 2026): the Net Balance column, the 'Paid' / 'No rent' badges and the Unpaid / No Rent tabs
// (contracts.logic, ContractsTable and the worker) all read these helpers - there were two copies of the
// accrual that disagreed on the rate, on the day a month starts and on closed contracts.
//
// The rule is the Monthly Calculator's (MonthlyCalculator.js + rentRows.js), not a new formula:
//   (the owner's monthly rule: a month is 30 days, a shorter period is monthly amount / 30 x days, and the
//   first month is always the full monthly amount - rentRows.js)
//   - the saved month rows count as they are: rent + VAT + Salik + parking + fines + other - paid
//     (the calculator's calculateBalance), so a row edited by hand keeps its figure
//   - an OPEN contract also owes the months the calculator would add up to today: 30-day periods from
//     the end of the last row (or the pick-up), each priced by rentForPeriodSchedule at the contract's
//     monthly rent (the rate schedule, else total_rent) - never Ejar's daily rental_rate (CON-04).
//     The first period is always owed; a period that starts today is not yet (the calculator's loop)
//   - the result is signed: below zero = the customer paid ahead (credit), above = owed. A stored
//     net_balance is used only when there is nothing to count from (no rows, and no rate or pick-up) -
//     it no longer replaces a credit with an old debt (CON-05)
//   - a CLOSED contract: its saved rows; the stored net_balance only when it has none
//   - 'today' is the Dubai calendar day (contractRules.dubaiToday), not the UTC date (CON-24)
//
// Pure: no React, no Firestore - the Contracts worker imports it.

import { parseSheetDate, formatDateDDMMYYYY } from './dates';
import { parseAmount, rentForPeriodSchedule, scheduleFromRows, baseRateFromRows } from './rentRows';
import { contractCategory, noMoneyNoteOf } from './contractRows';
import { dubaiToday, isOpenStatus } from '../../../services/contractRules';

const round2 = (n) => parseFloat((Number(n) || 0).toFixed(2));

const rowsOf = (doc) => (Array.isArray(doc?.monthly_calculations) ? doc.monthly_calculations : []);

const dateOf = (value) => (value ? parseSheetDate(String(value)) : null);

/** Open = status 'Open' (raw Firestore docs and page rows alike). */
export const isOpenContract = (doc) => isOpenStatus(doc);   // a blank status is not open

/**
 * The contract's monthly rent: total_rent (what the calculator saves and the engine learns), then the older
 * field names, then the saved rows' rate (rentRows.baseRateFromRows). Never rental_rate - that is Ejar's
 * DAILY rate (CON-04). The Invygo sheet's dealer_monthly_fee is gone with the sheet (owner, 28 Sep 2026).
 */
export const monthlyRateOf = (doc) => {
    const rate = parseAmount(doc?.total_rent) || parseAmount(doc?.['Total Rent']) || parseAmount(doc?.rent)
        || parseAmount(doc?.Rent);
    if (rate > 0) return rate;
    const rows = rowsOf(doc);
    return baseRateFromRows(rows);
};

/** A month row's rent: rent + VAT (what the calculator's balance adds), else its totalRent. */
const rowRent = (r) => (parseAmount(r?.rent) + parseAmount(r?.vat)) || parseAmount(r?.totalRent);

/** The calculator's calculateBalance of one month row. */
export const rowBalance = (r) => rowRent(r) + parseAmount(r?.salik) + parseAmount(r?.parking)
    + parseAmount(r?.fine) + parseAmount(r?.other) - parseAmount(r?.paid);

const storedBalance = (doc) => parseAmount(doc?.net_balance) || parseAmount(doc?.['Net Balance']) || parseAmount(doc?.netBalance);

/**
 * Rent of the months an open contract has no row for yet, up to `today` - the calculator's extension loop
 * (MonthlyCalculator loadData step 3, never clamped for an open contract).
 */
const missingMonthsRent = (rows, pickup, rate, schedule, today) => {
    let start = rows.length ? dateOf(rows[rows.length - 1].end) : new Date(pickup.getTime());
    if (!start) return 0;
    start.setUTCHours(0, 0, 0, 0);
    let count = rows.length;
    let rent = 0;
    for (let guard = 0; guard < 1200; guard++) {
        if (start.getTime() >= today.getTime() && count > 0) break;
        const end = new Date(start.getTime());
        end.setUTCDate(end.getUTCDate() + 30);
        if (rate > 0) {
            rent += rentForPeriodSchedule({
                start: formatDateDDMMYYYY(start), end: formatDateDDMMYYYY(end), schedule, fallbackBase: rate,
                first: count === 0
            }).total;
        }
        count++;
        if (start.getTime() >= today.getTime()) break;
        start = end;
    }
    return rent;
};

/**
 * The contract's balance now: > 0 owed, < 0 credit (paid ahead), 0 settled.
 * @param {Object} doc    a raw contract doc or a page row (both field spellings are read)
 * @param {Date}   today  the Dubai calendar day at UTC midnight (defaults to now)
 */
export const liveBalance = (doc, today = dubaiToday()) => {
    const rows = rowsOf(doc);
    if (!isOpenContract(doc)) {
        return round2(rows.length ? rows.reduce((acc, r) => acc + rowBalance(r), 0) : storedBalance(doc));
    }
    const pickup = dateOf(doc.pickup_date || doc['Pick-up Date'] || doc.pickupDate);
    const rate = monthlyRateOf(doc);
    if (!rows.length && !(pickup && rate > 0)) return round2(storedBalance(doc));   // nothing to count from

    // saved rows (the calculator drops a later zero-day row that carries no payment and no charge)
    const kept = rows.filter((r, i) => i === 0 || r?.start !== r?.end || parseAmount(r?.paid) > 0
        || ['salik', 'parking', 'fine', 'other'].some((f) => parseAmount(r?.[f]) !== 0));
    let balance = kept.reduce((acc, r) => acc + rowBalance(r), 0);
    if (pickup) {
        const schedule = Array.isArray(doc.rate_schedule) && doc.rate_schedule.length > 0
            ? doc.rate_schedule
            : scheduleFromRows(rows, rate);
        balance += missingMonthsRent(rows, pickup, rate, schedule, today);
    }
    return round2(balance);
};

/**
 * 'No rent' - the red badge in the table AND the No Rent tab (CON-11): an open contract, not Invygo (D04 /
 * the Invygo branch - its account is settled with management), whose first month row has no rent, or - with
 * no rows yet - that carries no rent at all (monthly total_rent or a daily rental_rate).
 */
export const hasNoRent = (row) => {
    // an Invygo or company contract: settled with the accountant, never "no rent" here (owner, 1 Oct 2026)
    if (!isOpenContract(row) || noMoneyNoteOf(row)) return false;
    const rows = rowsOf(row);
    if (rows.length > 0) return !rowRent(rows[0]);
    return !(parseAmount(row.total_rent) || parseAmount(row['Total Rent']) || parseAmount(row.rent)
        || parseAmount(row.Rent) || parseAmount(row.rental_rate) || parseAmount(row['Rental Rate']));
};

/**
 * An Invygo or company contract (the Net Balance column and the Excel's balance columns): shown '—' with
 * contractRows' note, never a balance, 'Paid' or a debt - its money is settled with the accountant and Ejar shows no
 * payment for it (owner, 28 Sep / 1 Oct 2026: "عايز يظهر - بس تكون كل حاجه متسجله"). '' for any other contract.
 */
export const noBalanceNote = (row) => noMoneyNoteOf(row);

/** The Excel's Net Balance cell: what the column shows - '—' for an Invygo or company contract, else the balance. */
export const netBalanceExport = (row) => (noBalanceNote(row) ? '—' : row?.netBalance);

/**
 * The Unpaid tab: an open contract that owes (live balance > 0) and has no payment in any month row. Never Invygo:
 * it never pays Yelo, so "no payment" is its normal state, not a debt to chase (owner, 28 Sep 2026 - as No Rent).
 */
export const isUnpaid = (row, today = dubaiToday()) => {
    if (!isOpenContract(row) || noMoneyNoteOf(row)) return false;      // Invygo / company: never a debt to chase
    const paid = rowsOf(row).reduce((sum, r) => sum + parseAmount(r?.paid || r?.Paid), 0);
    return paid === 0 && liveBalance(row, today) > 0;
};

/**
 * A DAILY contract's balance is Ejar's own (owner, 26 Sep 2026: "the daily amount straight from Ejar") - Yelo
 * computes only the monthly ones. `ejarBalances` = the engine's saved read of Ejar (services/ejarBalances).
 * Returns null for a contract that is not daily (the column shows the live balance), else
 * { balance: number | null, source, at } - null when Ejar's figure is not known yet (the cell shows '—',
 * never 'Paid' from a stored 0).
 */
export const dailyEjarBalance = (row, ejarBalances) => {
    if (contractCategory(row) !== 'Daily') return null;
    const cn = String(row?.contractNo || row?.['Contract No.'] || row?.contract_no || '').trim().toUpperCase();
    const hit = (ejarBalances && cn && ejarBalances[cn]) || null;
    const balance = hit && typeof hit.balance === 'number' && Number.isFinite(hit.balance) ? hit.balance : null;
    return { balance, source: hit ? hit.source : null, at: hit ? hit.at : null };
};
